import clsx from 'clsx';
import equal from 'fast-deep-equal';
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PopperProps } from '@mui/material';
import RefreshIcon from 'assets/icons/refresh_24.svg?react';
import {
  TextButton,
  TextButtonColor,
} from 'components/uikit/buttons/TextButton';
import { AppPopper } from 'components/uikit/menu/AppPopper';
import { CascadingFilter } from './CascadingFilter';
import { CascadeLevel, CascadeSelection } from './types';

import styles from './CascadingFilterButton.module.scss';

export type CascadingFilterButtonProps<TValue> = {
  root: CascadeLevel<TValue>;
  value: CascadeSelection<TValue>;
  onChange: (value: CascadeSelection<TValue>) => void;

  icon?: ReactNode;
  title?: string;
  color?: TextButtonColor;

  resetTitle?: string;
  hideReset?: boolean;

  /** Overrides the indicator on the button. By default it shows when the selection is not empty. */
  active?: boolean;

  placement?: PopperProps['placement'];
  disabled?: boolean;

  className?: string;
  panelClassName?: string;
  levelClassName?: string;

  testId?: string;
};

export function CascadingFilterButton<TValue>(
  props: CascadingFilterButtonProps<TValue>,
) {
  const {
    root,
    value,
    onChange,
    icon,
    title,
    color = TextButtonColor.Primary,
    resetTitle,
    hideReset,
    active,
    placement = 'bottom-start',
    disabled,
    className,
    panelClassName,
    levelClassName,
    testId,
  } = props;

  const i18next = useTranslation();

  const [isOpen, setIsOpen] = useState(false);
  const [draft, setDraft] = useState<CascadeSelection<TValue>>(value);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const draftRef = useRef(draft);
  draftRef.current = draft;
  const valueRef = useRef(value);
  valueRef.current = value;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const close = useCallback(() => {
    setIsOpen(false);

    if (!equal(draftRef.current, valueRef.current)) {
      onChangeRef.current(draftRef.current);
    }
  }, []);

  useEffect(
    function closeWhenDisabled() {
      if (disabled && isOpen) {
        close();
      }
    },
    [disabled, isOpen, close],
  );

  const isActive = active ?? Object.keys(isOpen ? draft : value).length > 0;

  return (
    <>
      <div
        className={clsx(styles.trigger, className)}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <TextButton
          ref={buttonRef}
          testId={testId}
          icon={icon}
          title={title}
          color={color}
          disabled={disabled}
          onClick={() => {
            if (isOpen) {
              close();
              return;
            }

            setDraft(value);
            setIsOpen(true);
          }}
        />
        {isActive && <span className={styles.indicator} />}
      </div>

      <AppPopper
        open={isOpen}
        anchorEl={buttonRef.current}
        placement={placement}
        noArrow={true}
        onClose={close}
        className={clsx(styles.panel, panelClassName)}
      >
        <div className={styles.popperContent}>
          <CascadingFilter
            root={root}
            value={draft}
            onChange={setDraft}
            levelClassName={levelClassName}
          />

          {!hideReset && (
            <div className={styles.reset}>
              <TextButton
                testId="cascading-filter-reset"
                color={TextButtonColor.Destroy}
                icon={<RefreshIcon />}
                title={
                  resetTitle ?? i18next.t('uikit.inputs.select_none_button')
                }
                onClick={() => setDraft({})}
                disabled={Object.keys(draft).length === 0}
              />
            </div>
          )}
        </div>
      </AppPopper>
    </>
  );
}
